import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

interface DataFreshnessIndicatorProps {
  dataFreshness: number; // minutes since last update
  confidence: number; // 0-1
  lastUpdated: string | null;
  onRefresh: () => void;
  isRefreshing: boolean;
}

export const DataFreshnessIndicator: React.FC<DataFreshnessIndicatorProps> = ({
  dataFreshness,
  confidence,
  lastUpdated,
  onRefresh,
  isRefreshing,
}) => {
  const getFreshnessStatus = () => {
    if (dataFreshness <= 5)
      return { status: 'fresh', color: '#10B981', icon: 'checkmark-circle' };
    if (dataFreshness <= 30)
      return { status: 'recent', color: '#F59E0B', icon: 'time' };
    return { status: 'stale', color: '#EF4444', icon: 'warning' };
  };

  const getConfidenceLevel = () => {
    if (confidence >= 0.9) return { level: 'High', color: '#10B981' };
    if (confidence >= 0.7) return { level: 'Medium', color: '#F59E0B' };
    return { level: 'Low', color: '#EF4444' };
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return 'Never';

    const now = new Date();
    const updated = new Date(lastUpdated);
    const diffMinutes = Math.floor(
      (now.getTime() - updated.getTime()) / (1000 * 60)
    );

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  const freshnessStatus = getFreshnessStatus();
  const confidenceLevel = getConfidenceLevel();

  return (
    <View style={styles.container}>
      <View style={styles.indicatorsContainer}>
        <View style={styles.indicator}>
          <View style={styles.indicatorHeader}>
            <Ionicons
              name={freshnessStatus.icon as any}
              size={16}
              color={freshnessStatus.color}
            />
            <Text style={styles.indicatorLabel}>Data Freshness</Text>
          </View>
          <Text
            style={[styles.indicatorValue, { color: freshnessStatus.color }]}
          >
            {formatLastUpdated()}
          </Text>
        </View>

        <View style={styles.separator} />

        <View style={styles.indicator}>
          <View style={styles.indicatorHeader}>
            <Ionicons
              name='analytics'
              size={16}
              color={confidenceLevel.color}
            />
            <Text style={styles.indicatorLabel}>AI Confidence</Text>
          </View>
          <Text
            style={[styles.indicatorValue, { color: confidenceLevel.color }]}
          >
            {confidenceLevel.level} ({Math.round(confidence * 100)}%)
          </Text>
        </View>
      </View>

      <TouchableOpacity
        style={[styles.refreshButton, isRefreshing && styles.refreshingButton]}
        onPress={onRefresh}
        disabled={isRefreshing}
      >
        <Ionicons
          name={isRefreshing ? 'sync' : 'refresh'}
          size={16}
          color={isRefreshing ? '#9CA3AF' : '#6750A4'}
          style={isRefreshing ? styles.spinning : undefined}
        />
        <Text
          style={[styles.refreshText, isRefreshing && styles.refreshingText]}
        >
          {isRefreshing ? 'Updating...' : 'Refresh'}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  indicatorsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  indicator: {
    alignItems: 'flex-start',
  },
  indicatorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
    gap: 4,
  },
  indicatorLabel: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  indicatorValue: {
    fontSize: 13,
    fontWeight: '600',
  },
  separator: {
    width: 1,
    height: 32,
    backgroundColor: '#E5E7EB',
    marginHorizontal: 16,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#EDE7F6',
    borderRadius: 16,
    gap: 4,
  },
  refreshingButton: {
    backgroundColor: '#F3F4F6',
  },
  refreshText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6750A4',
  },
  refreshingText: {
    color: '#9CA3AF',
  },
  spinning: {
    // Add rotation animation if needed
  },
});
